import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, Maximize2Icon, XIcon } from 'lucide-react';
import { INSIDE_FOOTNOTE, INSIDE_SUB, INSIDE_TITLE, insidePages } from '../data/resinContent';
const EASE = [0.23, 1, 0.32, 1] as const;
const TOTAL = insidePages.length;
export function EbookCarouselSection() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [index, setIndex] = useState(0);
  const [zoomed, setZoomed] = useState<number | null>(null);
  const go = useCallback((next: number) => {
    const track = trackRef.current;
    if (!track) return;
    const target = (next + TOTAL) % TOTAL;
    track.scrollTo({
      left: target * track.clientWidth,
      behavior: 'smooth'
    });
    setIndex(target);
  }, []);

  // Keep the active index in sync with native swipe.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const i = Math.round(track.scrollLeft / track.clientWidth);
        setIndex(Math.min(Math.max(i, 0), TOTAL - 1));
      });
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => {
      track.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);
  const current = insidePages[index];
  const arrowClass = 'flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-champagne/40 text-champagne transition-colors duration-150 ease-out hover:bg-champagne hover:text-charcoal-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-champagne';
  return <section className="w-full border-y border-champagne/20 bg-black py-12 sm:py-16" aria-labelledby="carousel-title" data-id="element-77" data-mp="3mclekr4oftff">
      <div className="mx-auto max-w-6xl px-5" data-id="element-78" data-mp="1s0oi2jrdnxdi">
        <h2 id="carousel-title" className="text-center font-display text-[1.8rem] font-semibold uppercase leading-tight text-ivory-50 sm:text-[2.5rem]" data-id="element-79" data-mp="1s1zlujktp54m">
          {INSIDE_TITLE}
        </h2>
        <p className="mt-2 text-center text-[11px] font-semibold uppercase tracking-[0.26em] text-champagne" data-id="element-80" data-mp="y8m0bro7jk">
          {INSIDE_SUB}
        </p>

        <div className="mt-8 flex items-center justify-center gap-3 sm:gap-6" data-id="element-81" data-mp="9xkvsvrkfaor">
          <button type="button" onClick={() => go(index - 1)} className={`hidden sm:flex ${arrowClass}`} data-id="element-82" data-mp="dymxrkcva0uf">
            <ChevronLeftIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-83" data-mp="ixgahakbxntj" />
            <span className="sr-only" data-id="element-84" data-mp="nog3w5u8pnra">Previous page</span>
          </button>

          <div className="w-full max-w-[26rem] sm:max-w-[30rem]" data-id="element-85" data-mp="nqj4tkwxl1as">
            <ul ref={trackRef} className="no-scrollbar flex snap-x snap-mandatory overflow-x-auto" aria-label="Real pages from Resin to Profit™" data-id="element-86" data-mp="sm7w70ysrbw0">
              {insidePages.map((page, i) => <li key={page.src} className="w-full shrink-0 snap-center px-1" data-id="element-87" data-mp="2m69v6r8jqp5a" data-mpi={i}>
                  <button type="button" onClick={() => setZoomed(i)} className="group relative block w-full border border-champagne/25 bg-ivory-50 shadow-[0_24px_60px_rgba(0,0,0,0.6)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-champagne" data-id="element-88" data-mp="2m45j3m0hujtx">
                    <img src={page.src} alt={page.alt} width={1080} height={1350} loading={i < 2 ? 'eager' : 'lazy'} className="block h-auto w-full" data-id="element-89" data-mp="35tpoemhdh9x7" />
                    <span className="sr-only" data-id="element-90" data-mp="2r4thisr1y2y6">Enlarge page: {page.caption}</span>
                  </button>
                </li>)}
            </ul>
          </div>

          <button type="button" onClick={() => go(index + 1)} className={`hidden sm:flex ${arrowClass}`} data-id="element-91" data-mp="3jsz4lm5kw416">
            <ChevronRightIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-92" data-mp="34axvjwj2qg7e" />
            <span className="sr-only" data-id="element-93" data-mp="38due5n8m6cbc">Next page</span>
          </button>
        </div>

        <p className="mt-4 text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-champagne" aria-live="polite" data-id="element-94" data-mp="2uecm8z00uixr">
          {current.caption}
          <span className="ml-3 font-medium tracking-[0.14em] text-muted" data-id="element-95" data-mp="2zf0bbf4owppc">
            {index + 1} / {TOTAL}
          </span>
        </p>
        <p className="mt-1.5 flex items-center justify-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.16em] text-muted" data-id="element-96" data-mp="3s1bkhe6d3yxr">
          <Maximize2Icon className="h-3 w-3" strokeWidth={2} aria-hidden="true" data-id="element-97" data-mp="3dbvzketiinpz" />
          <span className="sm:hidden" data-id="element-98" data-mp="3ib0ab9w34tif">Swipe to browse · Tap to enlarge</span>
          <span className="hidden sm:inline" data-id="element-99" data-mp="9w8d1msxbau6">Click a page to enlarge</span>
        </p>

        <div className="mt-4 flex items-center justify-center gap-3 sm:hidden" data-id="element-100" data-mp="emosfcgb16j6">
          <button type="button" onClick={() => go(index - 1)} className={arrowClass} data-id="element-101" data-mp="eorrsn3ckc0k">
            <ChevronLeftIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-102" data-mp="1zb9dg61ym2iv" />
            <span className="sr-only" data-id="element-103" data-mp="1udhjz7lo28ck">Previous page</span>
          </button>
          <button type="button" onClick={() => go(index + 1)} className={arrowClass} data-id="element-104" data-mp="285nry6eo190x">
            <ChevronRightIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-105" data-mp="243am7hzx5b45" />
            <span className="sr-only" data-id="element-106" data-mp="1gdybuesuux09">Next page</span>
          </button>
        </div>

        <ul className="no-scrollbar mt-7 flex snap-x snap-mandatory gap-2.5 overflow-x-auto pb-1 sm:flex-wrap sm:justify-center sm:overflow-visible" data-id="element-107" data-mp="1bf4yqnhhd80m">
          {insidePages.map((page, i) => <li key={page.src} className="shrink-0 snap-start" data-id="element-108" data-mp="1pcjusb28oe1k" data-mpi={i}>
              <button type="button" onClick={() => go(i)} aria-current={i === index} className={`block w-[4.25rem] overflow-hidden border bg-ivory-50 transition-[border-color,opacity] duration-150 ease-out sm:w-[5rem] ${i === index ? 'border-champagne opacity-100' : 'border-charcoal-600 opacity-55 hover:opacity-100'}`} data-id="element-109" data-mp="1ke9x9tt1l1cs">
                <img src={page.src} alt="" width={1080} height={1350} loading="lazy" className="block h-auto w-full" data-id="element-110" data-mp="22i8v9yp733h3" />
                <span className="sr-only" data-id="element-111" data-mp="27hv9aacuknqy">View {page.caption}</span>
              </button>
            </li>)}
        </ul>

        <div className="mx-auto mt-9 max-w-2xl text-center" data-id="element-112" data-mp="1asdsaz8ezf9y">
          {INSIDE_FOOTNOTE.map((line, i) => <p key={line} className={i < 2 ? 'font-display text-xl font-semibold uppercase leading-snug text-ivory-50 sm:text-2xl' : 'mt-1 font-display text-lg italic leading-snug text-champagne sm:text-xl'} data-id="element-113" data-mp="1et7ftrhy2s2b" data-mpi={i}>
              {line}
            </p>)}
        </div>
      </div>

      <PageLightbox index={zoomed} onClose={() => setZoomed(null)} onChange={setZoomed} data-id="element-114" data-mp="w0vffaauq0ut" />
    </section>;
}
type PageLightboxProps = {
  index: number | null;
  onClose: () => void;
  onChange: (i: number) => void;
};
function PageLightbox({
  index,
  onClose,
  onChange
}: PageLightboxProps) {
  const open = index !== null;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && index !== null) onChange((index + 1) % TOTAL);
      if (e.key === 'ArrowLeft' && index !== null) onChange((index - 1 + TOTAL) % TOTAL);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, index, onClose, onChange]);
  const page = index !== null ? insidePages[index] : null;
  return <AnimatePresence data-id="element-115" data-mp="3e04rkni2v6d6">
      {page && index !== null && <motion.div role="dialog" aria-modal="true" aria-label={page.caption} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} className="fixed inset-0 z-50 flex flex-col bg-black/95" onClick={onClose} data-id="element-116" data-mp="15qetatmztoch">
          <div className="flex items-center justify-between px-4 py-3" onClick={e => e.stopPropagation()} data-id="element-117" data-mp="10tocu57a0qqj">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-champagne" data-id="element-118" data-mp="rr5dhv2p8w91">
              {page.caption}
              <span className="ml-3 font-medium text-muted" data-id="element-119" data-mp="d30roi8co4m1">
                {index + 1} / {TOTAL}
              </span>
            </p>
            <button type="button" onClick={onClose} autoFocus className="flex h-11 w-11 items-center justify-center rounded-full text-ivory-100 transition-colors duration-150 ease-out hover:bg-charcoal-700" data-id="element-120" data-mp="i1vmyjni7ln1">
              <XIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-121" data-mp="3llb5ydnpij1y" />
              <span className="sr-only" data-id="element-122" data-mp="3glork0w56wrk">Close</span>
            </button>
          </div>

          <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-auto px-2 pb-4" data-id="element-123" data-mp="3ukokhkqotxtj">
            <motion.img key={page.src} src={page.src} alt={page.alt} initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.22,
          ease: EASE
        }} onClick={e => e.stopPropagation()} className="max-h-full w-auto max-w-full object-contain shadow-[0_24px_60px_rgba(0,0,0,0.6)]" data-id="element-124" data-mp="3qiti0cx0tee6" />
          </div>

          <div className="flex items-center justify-center gap-3 pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={e => e.stopPropagation()} data-id="element-125" data-mp="d6jdd3vhpok1">
            <button type="button" onClick={() => onChange((index - 1 + TOTAL) % TOTAL)} className="flex h-11 w-11 items-center justify-center rounded-full border border-champagne/40 text-champagne transition-colors duration-150 ease-out hover:bg-champagne hover:text-charcoal-950" data-id="element-126" data-mp="m5wf1addh8u7">
              <ChevronLeftIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-127" data-mp="2z4tjmylpwcol" />
              <span className="sr-only" data-id="element-128" data-mp="2u70d24gi5gns">Previous page</span>
            </button>
            <button type="button" onClick={() => onChange((index + 1) % TOTAL)} className="flex h-11 w-11 items-center justify-center rounded-full border border-champagne/40 text-champagne transition-colors duration-150 ease-out hover:bg-champagne hover:text-charcoal-950" data-id="element-129" data-mp="3rs64n4c8ccfs">
              <ChevronRightIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-130" data-mp="3cvowsn391fxz" />
              <span className="sr-only" data-id="element-131" data-mp="26hfs5g0sg9bv">Next page</span>
            </button>
          </div>
        </motion.div>}
    </AnimatePresence>;
}